import {
  Flex,
  Grid,
  Input,
  NativeSelect,
  RadioGroup,
  Text,
  useMediaQuery,
  Skeleton,
  SkeletonText,
  Stack,
  Image,
} from "@chakra-ui/react";
import { useEffect, useState, useMemo, useRef } from "react";
import { PRIMARY_COLOR } from "../../constants/colors";
import { BsTags } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import { toast } from "react-toastify";
import useFilterStore from "../../store/filterStore";

const normalizeImageUrl = (path) => {
  if (!path) return "";

  const normalized = path.replace(/\\/g, "/");

  const index = normalized.indexOf("/uploads");
  if (index === -1) return "";

  return `https://api.aqvex.com.br${normalized.substring(index)}`;
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [isShrunk, setIsShrunk] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const { clientHash, sellerId } = useParams();
  const navigate = useNavigate();

  const {
    filterText,
    selectedCategory,
    setSelectedCategory,
    selectedSubCategory,
    setSelectedSubCategory,
    orderBy,
    setOrderBy,
    categorySearch,
    setCategorySearch,
    clearFilter,
  } = useFilterStore();

  const scrollRef = useRef(null);

  useEffect(() => {
    if (!loadingSubCategories) {
      // espera o layout terminar de montar
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollLeft = 0;
        }
      }, 50);
    }
  }, [loadingSubCategories]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    localStorage.setItem("@aqvex/clientHash", clientHash);
    localStorage.setItem("@aqvex/sellerId", sellerId);
  }, [clientHash, sellerId]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 120) {
        setIsShrunk(true);
      } else {
        setIsShrunk(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const normalize = (str) =>
    str
      ? str
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
      : "";

  useEffect(() => {
    const getProductsByClient = async () => {
      setLoadingProducts(true);
      try {
        const cachedProducts = localStorage.getItem(
          `@aqvex/products_${clientHash}`,
        );
        const lastUpdate = localStorage.getItem(
          `@aqvex/products_last_update_${clientHash}`,
        );

        if (cachedProducts) setProducts(JSON.parse(cachedProducts));

        const shouldUpdate =
          !lastUpdate || new Date() - new Date(lastUpdate) > 1000 * 60 * 2;

        if (shouldUpdate) {
          const response = await api.get(
            `/client-database/products/${clientHash}`,
          );
          setProducts(response.data.data);
          localStorage.setItem(
            `@aqvex/products_${clientHash}`,
            JSON.stringify(response.data.data),
          );
          localStorage.setItem(
            `@aqvex/products_last_update_${clientHash}`,
            new Date().toISOString(),
          );
        }
      } catch (error) {
        toast.error(
          error?.message ?? "Não foi possível buscar o catálogo de produtos.",
        );
      } finally {
        setLoadingProducts(false);
      }
    };

    const getCategoriesByClient = async () => {
      setLoadingCategories(true);
      try {
        const cached = localStorage.getItem(`@aqvex/categories_${clientHash}`);
        const last = localStorage.getItem(
          `@aqvex/categories_last_update_${clientHash}`,
        );
        if (cached) setCategories(JSON.parse(cached));

        const shouldUpdate =
          !last || new Date() - new Date(last) > 1000 * 60 * 2;
        if (shouldUpdate) {
          const res = await api.get(
            `/client-database/categories/${clientHash}`,
          );
          setCategories(res.data.data);
          localStorage.setItem(
            `@aqvex/categories_${clientHash}`,
            JSON.stringify(res.data.data),
          );
          localStorage.setItem(
            `@aqvex/categories_last_update_${clientHash}`,
            new Date().toISOString(),
          );
        }
      } catch (error) {
        toast.error(error?.message ?? "Não foi possível buscar as categorias.");
      } finally {
        setLoadingCategories(false);
      }
    };

    const getSubCategoriesByClient = async () => {
      setLoadingSubCategories(true);
      try {
        const cached = localStorage.getItem(
          `@aqvex/sub_categories_${clientHash}`,
        );
        const last = localStorage.getItem(
          `@aqvex/sub_categories_last_update_${clientHash}`,
        );
        if (cached) setSubCategories(JSON.parse(cached));

        const shouldUpdate =
          !last || new Date() - new Date(last) > 1000 * 60 * 2;
        if (shouldUpdate) {
          const res = await api.get(
            `/client-database/sub-categories/${clientHash}`,
          );
          setSubCategories(res.data.data);
          localStorage.setItem(
            `@aqvex/sub_categories_${clientHash}`,
            JSON.stringify(res.data.data),
          );
          localStorage.setItem(
            `@aqvex/sub_categories_last_update_${clientHash}`,
            new Date().toISOString(),
          );
        }
      } catch (error) {
        toast.error(
          error?.message ?? "Não foi possível buscar as sub-categorias.",
        );
      } finally {
        setLoadingSubCategories(false);
      }
    };

    if (clientHash) {
      getProductsByClient();
      getCategoriesByClient();
      getSubCategoriesByClient();
    }
  }, [clientHash]);

  useEffect(() => {
    const trimmed = filterText.trim();

    if (!clientHash || !trimmed) {
      setSearchResults(null);
      return;
    }

    const timeout = setTimeout(async () => {
      setLoadingSearch(true);
      try {
        const response = await api.get(
          `/client-database/products/${clientHash}`,
          { params: { search: trimmed } },
        );
        setSearchResults(response.data.data);
      } catch (error) {
        toast.error(
          error?.message ?? "Não foi possível buscar o catálogo de produtos.",
        );
      } finally {
        setLoadingSearch(false);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [filterText, clientHash]);

  const filteredCategories = useMemo(() => {
    return categories.filter((c) =>
      normalize(c.grp_descricao).includes(normalize(categorySearch)),
    );
  }, [categories, categorySearch]);

  const filteredProducts = useMemo(() => {
    let result = [...(searchResults ?? products)];

    if (selectedCategory) {
      result = result.filter((p) => p.categoria === selectedCategory);
    }

    if (selectedSubCategory) {
      result = result.filter((p) => p.sub_categoria === selectedSubCategory);
    }

    // 🔽 ORDER BY
    if (orderBy) {
      result.sort((a, b) => {
        switch (orderBy) {
          case "menor_preco":
            return a.preco - b.preco;

          case "maior_preco":
            return b.preco - a.preco;

          case "mais_recentes":
            // supondo que exista um campo de data
            return (
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
            );

          default:
            return 0;
        }
      });
    }

    return result;
  }, [products, searchResults, selectedCategory, selectedSubCategory, orderBy]);

  const renderSkeletons = (count = 8) =>
    Array.from({ length: count }).map((_, i) => (
      <Stack
        key={i}
        gap="6"
        width="100%"
        boxShadow="0 2px 8px rgba(0,0,0,0.1)"
        borderRadius="12px"
        padding="10px"
      >
        <Skeleton height="300px" borderTopRadius="12px" />
        <SkeletonText mt="4" noOfLines={3} spacing="4" />
      </Stack>
    ));

  return (
    <Flex width="100%" flexDirection="column" paddingTop="80px">
      {/* 🔹 SUBCATEGORIAS */}
      <Flex
        ref={scrollRef}
        width="100%"
        padding="15px"
        backgroundColor="#f8f9fa"
        justifyContent="center"
        alignItems="center"
        gap="20px"
        overflowX="auto"
        borderBottom="1px solid #e0e0e0"
        flexWrap="nowrap"
        transition="all 0.4s ease"
        marginTop="-14px"
        position="fixed"
        height={isShrunk && !isLargerThan768 ? "0px" : "auto"}
        display={isShrunk && !isLargerThan768 ? "none" : "flex"}
        scrollPaddingLeft={isLargerThan768 ? "250px" : "200px"}
        paddingLeft={isLargerThan768 && "250px"}
      >
        {loadingSubCategories ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height="30px" width="100px" borderRadius="16px" />
          ))
        ) : (
          <>
            {(() => {
              const isInicioSelected =
                !selectedCategory &&
                !selectedSubCategory &&
                !filterText &&
                !orderBy &&
                !categorySearch;
              return (
                <Text
                  px="15px"
                  py="10px"
                  borderRadius="16px"
                  border={
                    isInicioSelected
                      ? `2px solid ${PRIMARY_COLOR}`
                      : "1px solid #ddd"
                  }
                  cursor="pointer"
                  whiteSpace="nowrap"
                  marginLeft={!isLargerThan768 ? "90px" : "0px"}
                  fontSize={isLargerThan768 ? "16px" : "14px"}
                  backgroundColor={isInicioSelected ? PRIMARY_COLOR : "white"}
                  color={isInicioSelected ? "white" : "black"}
                  fontWeight={isInicioSelected ? "600" : "400"}
                  transition="all 0.2s ease"
                  onClick={() => clearFilter()}
                >
                  INICIO
                </Text>
              );
            })()}
            {subCategories.map(({ sgp_id, sgp_descricao }) => {
              const isSelected = selectedSubCategory === sgp_descricao;
              return (
                <Text
                  key={sgp_id}
                  px="15px"
                  py="10px"
                  borderRadius="16px"
                  border={
                    isSelected ? `2px solid ${PRIMARY_COLOR}` : "1px solid #ddd"
                  }
                  cursor="pointer"
                  whiteSpace="nowrap"
                  fontSize={isLargerThan768 ? "16px" : "14px"}
                  backgroundColor={isSelected ? PRIMARY_COLOR : "white"}
                  color={isSelected ? "white" : "black"}
                  fontWeight={isSelected ? "600" : "400"}
                  transition="all 0.2s ease"
                  onClick={() =>
                    setSelectedSubCategory(isSelected ? "" : sgp_descricao)
                  }
                >
                  {sgp_descricao}
                </Text>
              );
            })}
          </>
        )}
      </Flex>

      <Flex width="100%">
        {/* 🔹 SIDEBAR DE CATEGORIAS */}
        {(isLargerThan768 || isSidebarOpen) && (
          <>
            {!isLargerThan768 && (
              <Flex
                position="fixed"
                top="0"
                left="0"
                right="0"
                bottom="0"
                backgroundColor="rgba(0,0,0,0.5)"
                zIndex="999"
                onClick={() => setIsSidebarOpen(false)}
              />
            )}
          <Flex
            flexDirection="column"
            padding="15px"
            backgroundColor="#f8f9fa"
            borderRight="1px solid #e0e0e0"
            gap="20px"
            minW={isLargerThan768 ? "250px" : "80%"}
            w={isLargerThan768 ? "250px" : "80%"}
            marginTop={isLargerThan768 ? "-20px" : "0"}
            paddingTop={isLargerThan768 ? "40px" : "60px"}
            position="fixed"
            height="100vh"
            top={isLargerThan768 ? undefined : "0"}
            left="0"
            zIndex={isLargerThan768 ? "auto" : "1000"}
            overflowY="auto"
          >
            {!isLargerThan768 && (
              <Flex
                position="absolute"
                top="15px"
                right="15px"
                cursor="pointer"
                onClick={() => setIsSidebarOpen(false)}
                backgroundColor="#e0e0e0"
                borderRadius="50%"
                width="32px"
                height="32px"
                alignItems="center"
                justifyContent="center"
                fontWeight="bold"
                fontSize="16px"
              >
                ✕
              </Flex>
            )}
            <Flex
              backgroundColor="white"
              padding="15px"
              flexDirection="column"
              borderRadius="8px"
              border="1px solid #ddd"
              gap="20px"
            >
              <Text
                fontWeight={700}
                paddingBottom="10px"
                borderBottom={`2px solid ${PRIMARY_COLOR}`}
                display="inline-block"
              >
                Ordenar por:
              </Text>
              <NativeSelect.Root size="md">
                <NativeSelect.Field
                  placeholder="Selecione"
                  paddingLeft="10px"
                  value={orderBy}
                  onChange={(e) => setOrderBy(e.target.value)}
                >
                  <option value="menor_preco">Menor Preço</option>
                  <option value="maior_preco">Maior Preço</option>
                  <option value="mais_recentes">Mais Recentes</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Flex>

            {/* Categorias */}
            <Flex flexDirection="column" gap="20px">
              <Flex
                gap="10px"
                paddingBottom="10px"
                borderBottom={`2px solid ${PRIMARY_COLOR}`}
                alignItems="center"
              >
                <BsTags />
                <Text fontWeight={700}>Categorias</Text>
              </Flex>
              <Input
                type="text"
                placeholder="Buscar categoria..."
                backgroundColor="white"
                padding="15px"
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
              />

              <Flex
                maxH="500px"
                overflow="auto"
                flexDirection="column"
                gap="20px"
              >
                {loadingCategories ? (
                  <Stack gap="3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} height="20px" />
                    ))}
                  </Stack>
                ) : filteredCategories.length === 0 ? (
                  <Text color="gray.500" fontSize="sm" mt={2}>
                    Nenhuma categoria encontrada.
                  </Text>
                ) : (
                  filteredCategories.map(({ grp_id, grp_descricao }) => {
                    const isSelected = selectedCategory === grp_descricao;
                    return (
                      <Flex
                        key={grp_id}
                        width="100%"
                        borderBottom="1px solid #ddd"
                        justifyContent="space-between"
                        alignItems="center"
                        paddingBottom="10px"
                        cursor="pointer"
                        onClick={() =>
                          setSelectedCategory(isSelected ? "" : grp_descricao)
                        }
                      >
                        <Flex gap="10px" alignItems="center">
                          <Text
                            color={isSelected ? PRIMARY_COLOR : ""}
                            fontWeight={isSelected ? "700" : "400"}
                          >
                            {grp_descricao}
                          </Text>
                        </Flex>
                      </Flex>
                    );
                  })
                )}
              </Flex>
            </Flex>
          </Flex>
          </>
        )}

        {/* 🔹 PRODUTOS */}
        <Flex
          padding={
            isLargerThan768 ? "90px 30px 30px 280px" : "100px 5px 5px 5px"
          }
          width="100%"
          flexDirection="column"
        >
          {!isLargerThan768 && (
            <Flex padding="0 5px 10px" gap="10px" alignItems="center">
              <Flex
                cursor="pointer"
                backgroundColor={PRIMARY_COLOR}
                color="white"
                padding="8px 16px"
                borderRadius="8px"
                alignItems="center"
                gap="8px"
                onClick={() => setIsSidebarOpen(true)}
              >
                <BsTags size={14} />
                <Text fontWeight="600" fontSize="14px">
                  Filtros
                </Text>
              </Flex>
              {selectedCategory && (
                <Flex
                  alignItems="center"
                  gap="6px"
                  backgroundColor="#eee"
                  borderRadius="8px"
                  padding="6px 10px"
                >
                  <Text fontSize="13px">{selectedCategory}</Text>
                  <Text
                    cursor="pointer"
                    fontWeight="bold"
                    fontSize="13px"
                    color={PRIMARY_COLOR}
                    onClick={() => setSelectedCategory("")}
                  >
                    ✕
                  </Text>
                </Flex>
              )}
            </Flex>
          )}
          <Grid
            templateColumns={isLargerThan768 ? "1fr 1fr 1fr 1fr" : "1fr 1fr"}
            gap={isLargerThan768 ? "20px" : "10px"}
            width="100%"
            alignItems="flex-start"
            overflow="auto"
          >
            {loadingProducts || loadingSearch
              ? renderSkeletons()
              : filteredProducts.map((product, index) => (
                  <Flex
                    key={index}
                    boxShadow="0 2px 8px rgba(0,0,0,0.1)"
                    borderRadius="12px"
                    flexDirection="column"
                    onClick={() =>
                      navigate(
                        `/produto/${product.prod_id}/${clientHash}/${sellerId}`,
                      )
                    }
                  >
                    <Image
                      src={normalizeImageUrl(product.url_imagem)}
                      height="300px"
                      width="100%"
                      objectFit="contain"
                      alt={product.prod_descricao}
                    />
                    <Flex borderTopRadius="12px" background="#f1f1f1" />
                    <Flex
                      flexDirection="column"
                      padding={isLargerThan768 ? "20px" : "10px"}
                      gap="10px"
                    >
                      <Text fontWeight="600">{product.prod_descricao}</Text>
                      <Text fontSize="14px" marginTop="-10px">
                        #{product.prod_referencia}
                      </Text>
                      <Text fontWeight="400">{product.categoria}</Text>
                      <Text color="green" fontWeight="700">
                        {product.preco.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </Text>
                    </Flex>
                  </Flex>
                ))}
          </Grid>
        </Flex>
      </Flex>
    </Flex>
  );
}
